import { useCallback, useEffect, useRef, useState } from 'react';
import { App } from 'antd';

import {
	createSavedFilter,
	deleteSavedFilter,
	demoteSavedFilter,
	getSavedFilterLists,
	promoteSavedFilter,
	updateSavedFilter,
} from '../services/savedFilters';
import { sortThenPayload } from '../utils/sortLevels';
import { withSavedFilterView } from '../utils/savedFilterView';
import { normalizeId } from '../utils/ids';
import { describeQueryWarnings } from '../utils/collectionLinks';

/**
 * The collection's saved filters, in the FavoritesContext idiom: every
 * mutation applies optimistically and rolls the whole list back if the server
 * refuses. No provider — saved filters are scoped to one collection and only
 * the items page asks for them, so a `let active = true` fetch in this hook is
 * the honest amount of machinery.
 *
 * Mutations that a user can plausibly get WRONG (a duplicate name → 409) still
 * reject after rolling back, so the calling modal can show the message inline
 * instead of a toast the user has already dismissed by the time they retype.
 */

/** The two tiers a saved filter can live in — the same split as favorites. */
export const FILTER_SCOPE = { COLLECTION: 'collection', USER: 'user' };

const tierRank = scope => (scope === FILTER_SCOPE.COLLECTION ? 0 : 1);

/** Collection tier first, name order within each — the server's own ordering. */
export const sortSavedFilters = list => [...list].sort((a, b) => (
	tierRank(a?.scope) - tierRank(b?.scope)
	|| (a?.name || '').localeCompare(b?.name || '')
));

const replaceById = (list, id, next) => sortSavedFilters(
	list.map(entry => (normalizeId(entry?._id) === id ? { ...entry, ...next } : entry))
);

export default function useSavedFilters(collectionId) {
	const { message } = App.useApp();
	// The collection's own views only: a sub-collection inherits none from
	// the collections above it (owner ruling 2026-10-21, KPR-1084).
	const [filters, setFilters] = useState([]);
	const [loading, setLoading] = useState(Boolean(collectionId));
	// A failed read is not the same as having no saved filters (KPR-728): the
	// picker says so, with Retry. `loaded` is the first read having succeeded.
	const [loadError, setLoadError] = useState(null);
	const [loaded, setLoaded] = useState(false);
	const [attempt, setAttempt] = useState(0);
	// The committed list, for the rollback snapshot: a state updater may run
	// lazily (during the next render) rather than at the call, so a snapshot
	// taken only inside one is not guaranteed to exist by the time the
	// mutation fails — and a rollback to `undefined` breaks every consumer.
	const filtersRef = useRef(filters);
	filtersRef.current = filters;

	useEffect(() => {
		if (!collectionId) {
			setFilters([]);
			setLoading(false);
			setLoaded(false);
			setLoadError(null);
			return undefined;
		}
		let active = true;
		setLoading(true);
		getSavedFilterLists(collectionId)
			.then(({ filters: list }) => {
				if (!active) return;
				setFilters(sortSavedFilters(list));
				setLoadError(null);
				setLoaded(true);
			})
			// Saved filters are an enhancement over a page that works without
			// them, so the page carries on; the picker says the read failed and
			// offers Retry. Views already held stay.
			.catch((error) => { if (active) setLoadError(error || true); })
			.finally(() => { if (active) setLoading(false); });
		return () => { active = false; };
	}, [collectionId, attempt]);

	const reload = useCallback(() => setAttempt(n => n + 1), []);

	/**
	 * Applies `optimistic` immediately, runs `mutation`, and restores the
	 * previous list if it fails. `rethrow` lets a caller (the name modal) show
	 * the server's own message where the user is still typing.
	 */
	const run = useCallback(async (optimistic, mutation, { errorText, rethrow = false } = {}) => {
		let snapshot = filtersRef.current;
		setFilters(current => { snapshot = current; return optimistic(current); });
		try {
			const answer = await mutation();
			// A saved filter that walks a lookup into another collection
			// (`cross_collection_hop`) saves, with a warning: each reader's own
			// access there decides what it matches. Said once, not stored.
			const { warnings, ...result } = answer || {};
			const warningText = describeQueryWarnings(warnings, 'saved filter');
			if (warningText) message.warning(warningText);
			if (answer) {
				const id = normalizeId(result._id);
				setFilters(current => {
					const known = current.some(entry => normalizeId(entry?._id) === id);
					return known ? replaceById(current, id, result) : sortSavedFilters([...current, result]);
				});
			}
			return answer ? result : answer;
		} catch (error) {
			setFilters(snapshot);
			if (rethrow) throw error;
			message.error(error?.response?.data?.message || errorText);
			return null;
		}
	}, [message]);

	// The temporary row a POST shows before the server answers. Its id is a
	// placeholder: the real row replaces it wholesale on success. It reads as
	// the server's row will (`sort_then` a list or null, KPR-1636; card,
	// columns and view each a value or null, KPR-1641).
	const save = useCallback((draft) => {
		const pending = { ...withSavedFilterView(draft), sort_then: sortThenPayload(draft?.sort_then), _id: `pending-${Date.now()}`, scope: FILTER_SCOPE.USER, pending: true };
		return run(
			current => sortSavedFilters([...current, pending]),
			() => createSavedFilter(collectionId, draft)
				// Drop the placeholder before the real row lands, so the list
				// never briefly shows the filter twice.
				.then(saved => { setFilters(cur => cur.filter(e => e !== pending)); return saved; })
				.catch(error => { setFilters(cur => cur.filter(e => e !== pending)); throw error; }),
			{ errorText: 'Could not save the filter.', rethrow: true }
		);
	}, [collectionId, run]);

	const rename = useCallback((filter, name) => {
		const id = normalizeId(filter?._id);
		return run(
			current => replaceById(current, id, { name }),
			() => updateSavedFilter(collectionId, id, { name }),
			{ errorText: 'Could not rename the filter.', rethrow: true }
		);
	}, [collectionId, run]);

	// Update (KPR-1642): writes what is on screen to the applied filter, every
	// part of it. The row reads as it will while the PATCH is out; a refusal
	// (403 for a collection filter the caller may not change, 404 for one that
	// is gone) rolls it back and rethrows, so the caller can say so or offer
	// the picks a refusal asks for.
	const update = useCallback((filter, patch) => {
		const id = normalizeId(filter?._id);
		const { elementChoices, ...fields } = patch || {};
		const shown = { ...withSavedFilterView({ ...filter, ...fields }), sort_then: sortThenPayload(fields.sort_then ?? filter?.sort_then) };
		return run(
			current => replaceById(current, id, shown),
			() => updateSavedFilter(collectionId, id, patch),
			{ errorText: 'Could not update the saved filter.', rethrow: true }
		);
	}, [collectionId, run]);

	const remove = useCallback((filter) => {
		const id = normalizeId(filter?._id);
		return run(
			current => current.filter(entry => normalizeId(entry?._id) !== id),
			() => deleteSavedFilter(collectionId, id).then(() => null),
			{ errorText: 'Could not delete the filter.' }
		);
	}, [collectionId, run]);

	const promote = useCallback((filter) => {
		const id = normalizeId(filter?._id);
		return run(
			current => replaceById(current, id, { scope: FILTER_SCOPE.COLLECTION }),
			() => promoteSavedFilter(collectionId, id),
			{ errorText: 'Could not pin the filter for everyone.' }
		);
	}, [collectionId, run]);

	const demote = useCallback((filter) => {
		const id = normalizeId(filter?._id);
		return run(
			current => replaceById(current, id, { scope: FILTER_SCOPE.USER }),
			() => demoteSavedFilter(collectionId, id),
			{ errorText: 'Could not unpin the filter.' }
		);
	}, [collectionId, run]);

	return { filters, loading, loadError, loaded, reload, save, rename, update, remove, promote, demote };
}
